import React, { useState, useEffect, useRef } from 'react';
import { Bold, Italic, Underline, Palette, Type, RotateCcw, ChevronDown } from 'lucide-react';

/**
 * Rich formatting toolbar for a single audit cell. Sits between the
 * template-preview strip and the textarea in ScriptAuditPage's right pane.
 *
 * Applies uniformly to the whole cell body — no inline segments. Empty
 * values mean "use PDF template default"; the reset button wipes the
 * formatting doc entirely.
 *
 * Props:
 *   value         — {font_family, font_size, color, bold, italic, underline}
 *   fonts         — [{key, display_name, sample}]
 *   onChange(patch)  — partial update, patched immediately (auto-save)
 *   onReset()     — wipe entire formatting doc for this cell
 *   selectedCount — # of picker rows selected for bulk apply (0 → hide bulk btn)
 *   onBulkApply() — copy this cell's formatting to all selected cells
 */
export default function FormattingToolbar({ value, fonts, onChange, onReset, selectedCount, onBulkApply, bulletMode }) {
  const v = value || {};
  const [fontMenuOpen, setFontMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!fontMenuOpen) return;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setFontMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [fontMenuOpen]);

  const currentFont = fonts.find(f => f.key === v.font_family) || null;

  return (
    <div data-testid="formatting-toolbar" className="mb-2 p-2 bg-slate-50/70 border border-slate-200 rounded-md">
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* Font picker (dropdown with sample beside each option) */}
        <div className="relative" ref={menuRef}>
          <button
            data-testid="font-picker-btn"
            onClick={() => setFontMenuOpen(o => !o)}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-slate-700 border border-slate-300 bg-white rounded hover:bg-slate-100 min-w-[130px]"
            title="Font family"
          >
            <Type size={11} />
            <span className="truncate">{currentFont?.display_name || 'Default font'}</span>
            <ChevronDown size={10} className="ml-auto" />
          </button>
          {fontMenuOpen && (
            <div data-testid="font-picker-menu" className="absolute top-full left-0 mt-1 w-[280px] max-h-[320px] overflow-y-auto bg-white border border-slate-300 rounded-md shadow-lg z-30">
              <button
                data-testid="font-option-default"
                onClick={() => { onChange({ font_family: null }); setFontMenuOpen(false); }}
                className={`w-full text-left px-2 py-1.5 text-[10px] hover:bg-slate-100 border-b border-slate-100 ${!v.font_family ? 'bg-emerald-50' : ''}`}
              >
                <div className="font-bold text-slate-700">— Use template default —</div>
              </button>
              {fonts.map(f => (
                <button
                  key={f.key}
                  data-testid={`font-option-${f.key}`}
                  onClick={() => { onChange({ font_family: f.key }); setFontMenuOpen(false); }}
                  className={`w-full text-left px-2 py-1.5 text-[10px] hover:bg-slate-100 border-b border-slate-100 ${v.font_family === f.key ? 'bg-emerald-50' : ''}`}
                  title={f.display_name}
                >
                  <div className="font-bold text-slate-800">{f.display_name}</div>
                  <div className="text-slate-500 italic truncate" style={{ fontFamily: _cssFamilyFor(f.key) }}>{f.sample}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Size */}
        <input
          data-testid="font-size-input"
          type="number"
          min="6"
          max="72"
          step="0.5"
          value={v.font_size ?? ''}
          onChange={e => onChange({ font_size: e.target.value === '' ? null : parseFloat(e.target.value) })}
          placeholder="pt"
          className="w-14 px-1.5 py-1 text-[10px] font-mono border border-slate-300 rounded bg-white"
          title="Font size in points (empty = default)"
        />

        {/* Colour picker */}
        <label className="flex items-center gap-1 px-1.5 py-1 border border-slate-300 rounded bg-white text-[10px] cursor-pointer" title="Font colour">
          <Palette size={11} className="text-slate-500" />
          <input
            data-testid="font-color-input"
            type="color"
            value={v.color || '#000000'}
            onChange={e => onChange({ color: e.target.value })}
            className="w-5 h-5 border-0 bg-transparent cursor-pointer"
          />
          {v.color && (
            <button
              data-testid="font-color-clear"
              onClick={(e) => { e.preventDefault(); onChange({ color: null }); }}
              className="text-slate-400 hover:text-rose-600 text-[10px]"
              title="Clear colour"
            >✕</button>
          )}
        </label>

        {/* B / I / U trio */}
        <button
          data-testid="format-bold"
          onClick={() => onChange({ bold: !v.bold })}
          className={`w-7 h-7 flex items-center justify-center text-[11px] font-bold border rounded ${v.bold ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'}`}
          title="Bold"
        ><Bold size={12} /></button>
        <button
          data-testid="format-italic"
          onClick={() => onChange({ italic: !v.italic })}
          className={`w-7 h-7 flex items-center justify-center text-[11px] italic border rounded ${v.italic ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'}`}
          title="Italic"
        ><Italic size={12} /></button>
        <button
          data-testid="format-underline"
          onClick={() => onChange({ underline: !v.underline })}
          className={`w-7 h-7 flex items-center justify-center text-[11px] underline border rounded ${v.underline ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'}`}
          title="Underline"
        ><Underline size={12} /></button>

        {/* Bullet-style picker — only shown when this cell renders a
            bullet list (currently: main_repair_options). Options fixed
            + a Custom slot for any single character. */}
        {bulletMode && (
          <div className="flex items-center gap-1 pl-1 ml-1 border-l border-slate-200" data-testid="bullet-picker-wrapper">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Bullet</span>
            <select
              data-testid="bullet-style-select"
              value={BULLET_PRESETS.some(p => p.char === v.bullet_char) ? v.bullet_char : (v.bullet_char ? '__custom__' : '')}
              onChange={e => {
                const sel = e.target.value;
                if (sel === '__custom__') {
                  // Seed a visible custom value so the input becomes
                  // reachable (a plain space triggers the "non-preset"
                  // predicate below and reveals the input).
                  if (!v.bullet_char || BULLET_PRESETS.some(p => p.char === v.bullet_char)) {
                    onChange({ bullet_char: ' ' });
                  }
                  return;
                }
                onChange({ bullet_char: sel || null });
              }}
              className="w-24 px-1 py-1 text-[11px] border border-slate-300 rounded bg-white"
              title="Choose the bullet character"
            >
              <option value="">— default (•) —</option>
              {BULLET_PRESETS.map(p => (
                <option key={p.char} value={p.char}>{p.char}  {p.label}</option>
              ))}
              <option value="__custom__">Custom…</option>
            </select>
            {(v.bullet_char && !BULLET_PRESETS.some(p => p.char === v.bullet_char)) && (
              <input
                data-testid="bullet-custom-input"
                type="text"
                maxLength={2}
                value={v.bullet_char || ''}
                onChange={e => onChange({ bullet_char: e.target.value || null })}
                className="w-10 px-1 py-1 text-[11px] text-center border border-slate-300 rounded bg-white font-mono"
                title="Custom bullet character(s)"
              />
            )}
          </div>
        )}

        {/* Reset */}
        <button
          data-testid="format-reset"
          onClick={onReset}
          className="ml-auto flex items-center gap-1 px-1.5 py-1 text-[10px] font-semibold text-slate-500 hover:text-rose-600 border border-slate-200 rounded"
          title="Reset formatting to template default"
        >
          <RotateCcw size={10} /> Reset
        </button>
      </div>

      {/* Bulk-apply button — only shows when other cells are selected */}
      {selectedCount > 0 && (
        <div className="mt-2 flex items-center gap-2 pt-2 border-t border-slate-200">
          <span className="text-[10px] font-semibold text-slate-600">
            {selectedCount} other cell{selectedCount === 1 ? '' : 's'} selected
          </span>
          <button
            data-testid="format-bulk-apply-btn"
            onClick={onBulkApply}
            className="ml-auto px-2.5 py-1 text-[10px] font-semibold bg-[#316536] text-white rounded hover:bg-[#264c2a]"
          >
            📋 Apply this formatting to {selectedCount} selected
          </button>
        </div>
      )}
    </div>
  );
}

// Maps our ReportLab font key → the CSS font-family the operator's browser
// will render. Google Fonts substitutes are loaded via ScriptAuditPage's
// <link> tag, and native Microsoft fonts fall back to the OS-installed
// version when available (Windows/Office machines).
function _cssFamilyFor(key) {
  const m = {
    'Helvetica':  'Helvetica, Arial, sans-serif',
    'Times-Roman':'"Times New Roman", Times, serif',
    'Courier':    '"Courier New", Courier, monospace',
    'Tinos':      'Tinos, "Times New Roman", serif',
    'Arimo':      'Arimo, Arial, sans-serif',
    'Carlito':    'Carlito, Calibri, sans-serif',
    'ComicNeue':  '"Comic Neue", "Comic Sans MS", cursive',
    'RobotoSlab': '"Roboto Slab", Rockwell, serif',
    'Copse':      'Copse, "Copperplate Gothic", serif',
  };
  return m[key] || 'inherit';
}

// Preset bullet characters for the bullet-style dropdown. Order matches
// the dropdown's visual order — most-common at the top.
const BULLET_PRESETS = [
  { char: '\u2022', label: 'Round (•)' },
  { char: '\u25E6', label: 'Open (◦)' },
  { char: '\u25AA', label: 'Square (▪)' },
  { char: '\u25B8', label: 'Triangle (▸)' },
  { char: '\u2713', label: 'Check (✓)' },
  { char: '\u2013', label: 'Dash (–)' },
  { char: '\u27A4', label: 'Arrow (➤)' },
];
